import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * Window CustomEvent names for the transcript ↔ player contract.
 *
 * 'transcript-seek' seeks players past their RAF feedback-prevention window
 * (a plain `videoCreator.time` write is ignored mid-playback).
 * 'transcript-pause' pauses every mounted player — it is a DELIBERATE
 * broadcast (opening the Translation Review workspace must silence all
 * audio), so it carries no scope token. Seeks are scoped; pauses are not.
 * (PLAT-10286)
 * 'transcript-index-jump' asks a mounted transcript to show a given index —
 * scoped, like a seek (PLAT-11061).
 * 'transcript-show-clip' asks a mounted transcript to turn its Clips marks on
 * and make a given clip the active one — scoped, like a seek (PLAT-12103).
 */
export const TRANSCRIPT_SEEK_EVENT = 'transcript-seek';
export const TRANSCRIPT_PAUSE_EVENT = 'transcript-pause';
export const TRANSCRIPT_INDEX_JUMP_EVENT = 'transcript-index-jump';
export const TRANSCRIPT_SHOW_CLIP_EVENT = 'transcript-show-clip';

export interface TranscriptSeekDetail {
  /** Seek target, in the dispatching surface's `videoCreator` time space. */
  time: number;
  /**
   * Scope token: the shared `videoCreator` instance whose players should
   * honor this seek. Players bound to a different creator ignore the event —
   * object identity is the sandbox boundary (the clip adapter's `story`
   * delegates to the real story, so storyId cannot discriminate). Omitted —
   * or a legacy bare-number detail — means global: every player honors it.
   */
  creator?: ITranscriptVideoCreator;
}

/**
 * Dispatch a creator-scoped seek. Pass the dispatching surface's own
 * `videoCreator` so the seek only reaches players sharing it; the background
 * page player ignores seeks from a clip-scoped review and vice versa.
 */
export function dispatchTranscriptSeek(
  time: number,
  creator?: ITranscriptVideoCreator,
): void {
  window.dispatchEvent(
    new CustomEvent<TranscriptSeekDetail>(TRANSCRIPT_SEEK_EVENT, {
      detail: { time, creator },
    }),
  );
}

export interface TranscriptIndexJumpDetail {
  /** Index into the dispatching surface's `transcriptElements`. */
  index: number;
  /**
   * The target token's `source_uuid ?? id`, when the caller has it. Preferred
   * over `index` on arrival, because what the transcript renders is a
   * *projection* of `transcriptElements` — `dedupeConsecutiveWhitespace` drops
   * doubled whitespace (which stitched clips produce at segment boundaries) and
   * the karaoke view flattens the array outright, so a raw index can point one
   * passage short. An id survives both. `index` remains the fallback for a
   * token with no id of its own.
   */
  tokenId?: string;
  /**
   * Scope token, read exactly as {@link TranscriptSeekDetail.creator} is: the
   * transcript bound to this `videoCreator` jumps and every other mounted one
   * ignores the event. Object identity is the boundary — the clip adapter and
   * the story-level store share a story id but not an instance.
   */
  creator?: ITranscriptVideoCreator;
}

/**
 * Ask the transcript on `creator`'s surface to land its cursor on `index` and
 * bring it into view — the jump the highlight prev/next buttons already
 * perform, reached from outside the transcript (PLAT-11061: the Reel Editor's
 * "Edit Transcript").
 *
 * An event rather than a `Transcript` prop: `Transcript.tsx` has four hosts, so
 * a prop for one of them is the other three's problem too (CLAUDE.md guardrail
 * 5, enforced by `Transcript.budget.test.ts`). `REQUEST_CURSOR_Y_LOCK_EVENT` is
 * the same shape — an external request delivered by window event.
 *
 * No-ops when no transcript is mounted on that surface, and the caller gets no
 * acknowledgement either way — so nothing may depend on the jump having run.
 */
export function dispatchTranscriptIndexJump(
  target: { index: number; tokenId?: string },
  creator?: ITranscriptVideoCreator,
): void {
  window.dispatchEvent(
    new CustomEvent<TranscriptIndexJumpDetail>(TRANSCRIPT_INDEX_JUMP_EVENT, {
      detail: { ...target, creator },
    }),
  );
}

export interface TranscriptShowClipDetail {
  /** The clip's `transcriptPosition`, which is how its mark is matched. */
  startIndex: number;
  endIndex: number;
  /** Scope token, read exactly as {@link TranscriptSeekDetail.creator} is. */
  creator?: ITranscriptVideoCreator;
}

/**
 * Ask the transcript on `creator`'s surface to switch its Clips marks on with
 * the clip at this position active — what saving from the transcript's own
 * context menu does, for a save made elsewhere on the surface (PLAT-12103: the
 * Co-Producer's Save as Clip). An event rather than a `Transcript` prop for the
 * reason {@link dispatchTranscriptIndexJump} gives.
 *
 * No-ops when no transcript with a Clips toggle is mounted on that surface, or
 * while a Show Only filter is on.
 */
export function dispatchTranscriptShowClip(
  position: { startIndex: number; endIndex: number },
  creator?: ITranscriptVideoCreator,
): void {
  window.dispatchEvent(
    new CustomEvent<TranscriptShowClipDetail>(TRANSCRIPT_SHOW_CLIP_EVENT, {
      detail: { ...position, creator },
    }),
  );
}

/**
 * Seek the playhead to the transcript element identified by `initialIndex`.
 *
 * Single source of truth for "click a translated phrase / aligned word → move
 * the playhead to that word's start". Both `Transcript.tsx`'s translated-view
 * `seekToIndex` and `AlignedBlocksView`'s left/right column clicks call this so
 * the behavior stays identical across the two surfaces.
 *
 * Resolution: find the KEPT `text` element whose `initial_index` matches and
 * read its `ts` (punctuation, and removed/cut elements, have no usable
 * timestamp). Requiring a kept, numeric-`ts` element matters for a montage
 * MOVE: the source word is left struck (`removed`, `ts` null) at its old
 * position and re-added (kept) at the new one — BOTH carry the same
 * `initial_index`. Matching the first element by index alone lands on the
 * struck ghost and the seek no-ops; the phrase lead was anchored on the kept
 * copy, so we seek to that copy's (new) time. Then:
 *   1. `dispatchTranscriptSeek(ts, videoCreator)` — reaches players past their
 *      RAF feedback-prevention window (a plain `time` write is ignored
 *      mid-playback), scoped to this creator (PLAT-10286).
 *   2. `videoCreator.setTime(ts, true)` — moves the shared time owner, pausing
 *      the renderer if it was playing.
 *   3. Resume the renderer only if it was playing AND an external player does
 *      NOT own playback (`suppressRendererPlayback`) — avoids a second audio
 *      source when the Translation Review compact player is in control.
 *
 * No-ops when the index doesn't resolve to a seekable element.
 */
export function seekToInitialIndex(
  videoCreator: ITranscriptVideoCreator,
  transcriptElements: readonly TranscriptElement[] | undefined,
  initialIndex: number,
): void {
  if (!transcriptElements) return;
  const el = transcriptElements.find(
    e =>
      e.initial_index === initialIndex &&
      e.type === 'text' &&
      e.state !== 'removed' &&
      e.state !== 'cut' &&
      typeof e.ts === 'number',
  );
  const ts =
    el && el.type === 'text' && typeof el.ts === 'number' ? el.ts : undefined;
  if (ts === undefined) return;

  const wasPlaying = videoCreator.isPlaying;
  dispatchTranscriptSeek(ts, videoCreator);
  void videoCreator.setTime(ts, true).then(() => {
    // Don't resume the renderer when an external player owns playback (the
    // Translation Review compact player) — second audio source (PLAT-10286).
    if (wasPlaying && !videoCreator.suppressRendererPlayback) {
      videoCreator.renderer?.play();
    }
  });
}
